import {
  EnvelopeIcon,
  IdentificationIcon,
  KeyIcon,
  LockClosedIcon,
  LockOpenIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import {
  Button,
  Card,
  CardBody,
  cn,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  Link,
} from '@heroui/react';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EditEmailDialog } from '@/app/components/edit-email-dialog.tsx';
import GenericAlert from '@/app/components/generic-alert.tsx';
import { NoData } from '@/app/components/no-data.tsx';
import { Paginator, usePagination } from '@/app/components/paginator.tsx';
import { getApiErrorMessage } from '@/app/helpers/api-error.helpers.ts';
import { formatDate } from '@/app/helpers/date.helpers.ts';
import { isReleasedStaffEmail, staffEmailCopy } from '@/app/helpers/staff-email.helpers.ts';
import { useAuth } from '@/app/hooks/auth-provider.tsx';
import toast from '@/app/toast.ts';
import { Avatar } from '@/primitives/avatar.tsx';
import {
  ResponsiveTable,
  TableTile,
  TableTileField,
  TableTileList,
} from '@/primitives/responsive-table.tsx';
import SecurityCheck from '@/primitives/security.check.tsx';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useSort,
} from '@/primitives/table.tsx';
import { TextLink } from '@/primitives/text.tsx';
import {
  useDeleteStaffMemberMutation,
  usePutToggleActiveStaffMemberMutation,
  useReleaseStaffMemberEmailMutation,
} from '@/slices/staff-api-slice.ts';
import { useSecurityContext } from '../../../hooks/security-provider.tsx';
import { hasPermission } from '../../../types/constants.ts';
import {
  type StaffMemberListModel,
  type StaffMemberModel,
  StaffRoleEnum,
} from '../../../types/staff-member.type.ts';
import { UserTypeEnum } from '../../../types/user-type.ts';

interface AlertProps {
  title: string;
  message: string;
  onConfirm: () => void;
  button?: any;
}

function StaffName({ staff }: { staff: StaffMemberListModel }) {
  return (
    <div>
      <div className='font-medium'>
        {staff.firstName} {staff.lastName}
      </div>
      {staff.title && <div className='mt-0.5 text-xs text-gray-500'>{staff.title}</div>}
    </div>
  );
}

function StaffDietaryInfo({ staff }: { staff: StaffMemberListModel }) {
  const dietaryRestrictions = staff.dietaryRestrictions
    .concat([staff.otherDietaryRestriction])
    .filter(x => !!x)
    .join(', ');
  const dietaryPreferences = staff.dietaryPreferences
    .concat([staff.otherDietaryPreference])
    .filter(x => !!x)
    .join(', ');
  const foodNotes = staff.foodNotes;
  const hasDietaryInfo = Boolean(dietaryRestrictions || dietaryPreferences || foodNotes);

  return (
    <div className='flex flex-col gap-1'>
      {!hasDietaryInfo && <div className='text-xs text-default-500'>--</div>}
      {dietaryRestrictions && (
        <div className='text-xs text-danger-500'>Restrictions: {dietaryRestrictions}</div>
      )}
      {dietaryPreferences && (
        <div className='text-xs text-secondary-500'>Preferences: {dietaryPreferences}</div>
      )}
      {foodNotes && <div className='text-xs italic text-default-600'>Food Notes: {foodNotes}</div>}
    </div>
  );
}

const StaffMemberTable = ({ data }: { data: StaffMemberListModel[] }) => {
  const { currentAccount } = useSecurityContext();
  const { user } = useAuth();
  const memoizedSortingDataAccessor = useCallback((value: StaffMemberListModel, col: string) => {
    switch (col) {
      case 'createdAt':
      case 'lastLoggedInAt': {
        const date = DateTime.fromISO(_.get(value, col));
        return date.isValid ? date : '';
      }
      default:
        return _.get(value, col);
    }
  }, []);
  const { sort, sortData, setSort } = useSort({
    data,
    memoizedSortingDataAccessor,
  });
  const pager = usePagination({
    data: sortData,
    resourceName: 'staff members',
  });
  const { pagedData } = pager;
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertProps, setAlertProps] = useState<AlertProps>(null);
  const [deleteStaff, { isLoading: deleteLoading }] = useDeleteStaffMemberMutation();
  const [releaseEmail, { isLoading: releaseLoading }] = useReleaseStaffMemberEmailMutation();
  const [editEmailOpen, setEditEmailOpen] = useState(false);
  const [editEmailUser, setEditEmailUser] = useState<StaffMemberListModel>(null);
  const [toggleActive, { isLoading: toggleLoading }] = usePutToggleActiveStaffMemberMutation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<StaffMemberListModel | null>(null);
  const navigate = useNavigate();
  const { impersonateUser } = useAuth();

  const onOpenDrawer = (value: StaffMemberListModel) => {
    setSelectedStaff(value);
    setDrawerOpen(true);
  };

  const onCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedStaff(null);
  };

  const canEditStaffAccounts =
    user?.userType === UserTypeEnum.Admin ||
    (user?.userType === UserTypeEnum.StaffMember &&
      hasPermission(user as StaffMemberModel, StaffRoleEnum.Manager, currentAccount?.id));

  const openEditEmail = (value: StaffMemberListModel) => {
    setEditEmailUser(value);
    setEditEmailOpen(true);
  };

  const openDeleteStaff = (value: StaffMemberListModel) => {
    setAlertProps({
      title: 'Delete Staff Member',
      message: staffEmailCopy.deleteConfirm(value.firstName, value.lastName),
      onConfirm: () => onDeleteStaff(value),
    });
    setAlertOpen(true);
  };

  const openReleaseEmail = (value: StaffMemberListModel) => {
    setAlertProps({
      title: staffEmailCopy.freeEmailTitle,
      message: staffEmailCopy.freeEmailConfirm(value.firstName, value.lastName),
      onConfirm: () => onReleaseEmail(value),
    });
    setAlertOpen(true);
  };

  const onReleaseEmail = (staff: StaffMemberListModel) => {
    releaseEmail(staff.key)
      .unwrap()
      .then(() => {
        toast.success(staffEmailCopy.freeEmailSuccess);
      })
      .catch(error => {
        toast.error(getApiErrorMessage(error, staffEmailCopy.freeEmailError));
      });
    setAlertOpen(false);
    setAlertProps(null);
  };

  const onDeleteStaff = (user: StaffMemberListModel) => {
    deleteStaff(user.key)
      .unwrap()
      .then(() => {
        toast.success(staffEmailCopy.deleteSuccess);
      })
      .catch(error => {
        toast.error(getApiErrorMessage(error, staffEmailCopy.deleteError));
      });
    setAlertOpen(false);
    setAlertProps(null);
  };

  const cancelAlert = () => {
    setAlertOpen(false);
    setAlertProps(null);
  };

  const deactivateStaff = (user: StaffMemberListModel) => {
    toggleActive(user)
      .unwrap()
      .then(() => {
        toast.success(`User has been deactivated.`);
      })
      .catch(() => {
        toast.error('There was an error attempting to deactivate the user.');
      });
    setAlertOpen(false);
    setAlertProps(null);
  };

  const openToggleActiveAlert = (user: StaffMemberListModel) => {
    if (user.active) {
      setAlertProps({
        title: `Deactivate ${user.firstName} ${user.lastName}`,
        message: `Are you sure you want to deactivate ${user.firstName} ${user.lastName}? They will no longer be able to access MeetingMD with this account.`,
        onConfirm: () => deactivateStaff(user),
      });
      setAlertOpen(true);
    } else {
      toggleActive(user)
        .unwrap()
        .then(() => toast.success('User has been activated.'))
        .catch(() => toast.error('There was an error attempting to active the user.'));
    }
  };

  const showUserTimes = useCallback(() => {
    return (
      user?.userType === UserTypeEnum.Admin ||
      (user?.userType === UserTypeEnum.StaffMember &&
        hasPermission(
          user as StaffMemberModel,
          StaffRoleEnum.SiteAdministrator,
          currentAccount?.id,
        ))
    );
  }, [user, currentAccount]);

  const canShowUserTimes = showUserTimes();
  const hasStaff = Boolean(pagedData?.length);

  return (
    <>
      <ResponsiveTable
        currentSort={sort}
        setCurrentSort={setSort}
        tiles={
          <TableTileList empty={!hasStaff} emptyContent={<NoData />}>
            {hasStaff &&
              pagedData.map(value => (
                <TableTile key={value.id} onClick={() => onOpenDrawer(value)}>
                  <div className='flex flex-col gap-3'>
                    <TableTileField label='Name'>
                      <StaffName staff={value} />
                    </TableTileField>
                    <TableTileField label='Email'>
                      <TextLink
                        href={`mailto:${value.emailAddress}`}
                        onClick={e => e.stopPropagation()}
                      >
                        {value.emailAddress}
                      </TextLink>
                    </TableTileField>
                    {canShowUserTimes && (
                      <>
                        <TableTileField label='Created'>
                          {formatDate(value.createdAt, DateTime.DATETIME_SHORT)}
                        </TableTileField>
                        <TableTileField label='Last Logged In'>
                          {formatDate(value.lastLoggedInAt, DateTime.DATETIME_SHORT, '--')}
                        </TableTileField>
                      </>
                    )}
                    <TableTileField label='Dietary Info'>
                      <StaffDietaryInfo staff={value} />
                    </TableTileField>
                  </div>
                </TableTile>
              ))}
          </TableTileList>
        }
      >
        <TableHead>
          <TableRow>
            <TableHeader sortColumn='lastName'>Name</TableHeader>
            <TableHeader sortColumn='emailAddress'>Email</TableHeader>
            {canShowUserTimes && (
              <>
                <TableHeader sortColumn='createdAt'>Created</TableHeader>
                <TableHeader sortColumn='lastLoggedInAt'>Last Logged In</TableHeader>
              </>
            )}
            <TableHeader>Dietary Info</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {!hasStaff && (
            <TableRow>
              <TableCell colSpan={canShowUserTimes ? 6 : 4}>
                <NoData />
              </TableCell>
            </TableRow>
          )}
          {hasStaff &&
            pagedData.map(value => (
              <TableRow
                key={value.id}
                className='cursor-pointer hover:bg-gray-50'
                onClick={() => onOpenDrawer(value)}
              >
                <TableCell>
                  <StaffName staff={value} />
                </TableCell>
                <TableCell>
                  <TextLink
                    href={`mailto:${value.emailAddress}`}
                    onClick={e => e.stopPropagation()}
                  >
                    {value.emailAddress}
                  </TextLink>
                </TableCell>
                <SecurityCheck
                  allowedUserTypes={[UserTypeEnum.Admin, UserTypeEnum.StaffMember]}
                  minimumStaffRole={StaffRoleEnum.SiteAdministrator}
                >
                  <TableCell>{formatDate(value.createdAt, DateTime.DATETIME_SHORT)}</TableCell>
                  <TableCell>
                    {formatDate(value.lastLoggedInAt, DateTime.DATETIME_SHORT, '--')}
                  </TableCell>
                </SecurityCheck>
                <TableCell>
                  <StaffDietaryInfo staff={value} />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </ResponsiveTable>
      <Paginator {...pager} />
      <GenericAlert
        isOpen={alertOpen}
        setIsOpen={setAlertOpen}
        {...alertProps}
        onCancel={() => cancelAlert()}
      />
      <EditEmailDialog isOpen={editEmailOpen} setIsOpen={setEditEmailOpen} user={editEmailUser} />
      <Drawer
        hideCloseButton
        isOpen={drawerOpen}
        onClose={onCloseDrawer}
        placement='right'
        size='lg'
      >
        <DrawerContent>
          <DrawerHeader className='flex items-center justify-between border-b bg-gradient-primary text-primary-950'>
            <h2 className='text-xl font-semibold'>Staff Details</h2>
            <Button isIconOnly variant='light' onPress={onCloseDrawer} color='default'>
              <XMarkIcon className='size-5' />
            </Button>
          </DrawerHeader>
          <DrawerBody className='p-6'>
            {selectedStaff && (
              <div className='flex flex-col gap-6'>
                <Card shadow='none' className='border border-gray-200 bg-default-50'>
                  <CardBody className='p-4'>
                    <div className='flex items-start gap-3 mb-4'>
                      <Avatar
                        src={selectedStaff.profilePhotoUrl}
                        initials={`${selectedStaff.firstName?.substring(0, 1)}${selectedStaff.lastName?.substring(0, 1)}`}
                        className='size-12'
                      />
                      <div className='flex-1'>
                        <h3 className='font-semibold text-gray-900'>
                          {selectedStaff.firstName} {selectedStaff.lastName}
                        </h3>
                        {selectedStaff.title && (
                          <p className='text-sm text-gray-600 mt-0.5'>{selectedStaff.title}</p>
                        )}
                      </div>
                    </div>

                    <div className='space-y-3 text-sm'>
                      <div>
                        <p className='text-xs text-gray-500 font-medium mb-1'>Email</p>
                        <a
                          href={`mailto:${selectedStaff.emailAddress}`}
                          className='text-primary-600 hover:text-primary-700'
                        >
                          {selectedStaff.emailAddress}
                        </a>
                      </div>

                      {showUserTimes() && (
                        <>
                          <div>
                            <p className='text-xs text-gray-500 font-medium mb-1'>Created</p>
                            <p className='text-gray-700'>
                              {formatDate(selectedStaff.createdAt, DateTime.DATETIME_SHORT)}
                            </p>
                          </div>
                          <div>
                            <p className='text-xs text-gray-500 font-medium mb-1'>Last Logged In</p>
                            <p className='text-gray-700'>
                              {formatDate(
                                selectedStaff.lastLoggedInAt,
                                DateTime.DATETIME_SHORT,
                                '--',
                              )}
                            </p>
                          </div>
                        </>
                      )}

                      {(() => {
                        const dietaryRestrictions = selectedStaff.dietaryRestrictions
                          .concat([selectedStaff.otherDietaryRestriction])
                          .filter(x => !!x);
                        const dietaryPreferences = selectedStaff.dietaryPreferences
                          .concat([selectedStaff.otherDietaryPreference])
                          .filter(x => !!x);
                        const foodNotes = selectedStaff.foodNotes;
                        const hasDietaryInfo =
                          dietaryRestrictions.length > 0 ||
                          dietaryPreferences.length > 0 ||
                          foodNotes;

                        if (!hasDietaryInfo) {
                          return null;
                        }

                        return (
                          <div className='pt-3 border-t border-gray-200'>
                            <p className='text-xs text-gray-500 font-medium mb-2'>
                              Dietary Information
                            </p>
                            <div className='space-y-2'>
                              {dietaryRestrictions.length > 0 && (
                                <div>
                                  <p className='text-xs text-warning-600 font-medium'>
                                    Restrictions:
                                  </p>
                                  <p className='text-gray-700'>{dietaryRestrictions.join(', ')}</p>
                                </div>
                              )}
                              {dietaryPreferences.length > 0 && (
                                <div>
                                  <p className='text-xs text-success-600 font-medium'>
                                    Preferences:
                                  </p>
                                  <p className='text-gray-700'>{dietaryPreferences.join(', ')}</p>
                                </div>
                              )}
                              {foodNotes && (
                                <div>
                                  <p className='text-xs text-gray-500 font-medium'>Notes:</p>
                                  <p className='text-gray-700'>{foodNotes}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </CardBody>
                </Card>

                <div>
                  <h6 className='text-sm font-semibold text-gray-900 mb-3'>Quick Actions</h6>
                  <div className='flex flex-wrap items-center gap-2'>
                    <SecurityCheck
                      allowedUserTypes={[UserTypeEnum.Admin, UserTypeEnum.StaffMember]}
                      minimumStaffRole={StaffRoleEnum.Manager}
                    >
                      <Button
                        size='sm'
                        variant='flat'
                        color='default'
                        as={Link}
                        href={`/staff/edit/${selectedStaff.key}`}
                        isLoading={deleteLoading || toggleLoading || releaseLoading}
                      >
                        <PencilIcon title='Edit staff' className='size-4' />
                        Edit Staff
                      </Button>
                      <Button
                        size='sm'
                        variant='flat'
                        color='default'
                        onPress={() => {
                          openToggleActiveAlert(selectedStaff);
                          onCloseDrawer();
                        }}
                        isLoading={toggleLoading}
                      >
                        {selectedStaff.active ? (
                          <LockClosedIcon className='size-4' />
                        ) : (
                          <LockOpenIcon className='size-4' />
                        )}
                        {selectedStaff.active ? 'Deactivate User' : 'Active User'}
                      </Button>
                    </SecurityCheck>
                    <SecurityCheck
                      allowedUserTypes={[UserTypeEnum.Admin, UserTypeEnum.StaffMember]}
                      minimumStaffRole={StaffRoleEnum.Manager}
                    >
                      <Button
                        size='sm'
                        variant='flat'
                        color='default'
                        onPress={() => {
                          openEditEmail(selectedStaff);
                          onCloseDrawer();
                        }}
                        isLoading={deleteLoading || toggleLoading || releaseLoading}
                      >
                        <EnvelopeIcon className='size-4' title='Edit email' />
                        Edit Email
                      </Button>
                    </SecurityCheck>
                    <SecurityCheck allowedUserTypes={[UserTypeEnum.Admin]}>
                      <Button
                        size='sm'
                        variant='flat'
                        color='primary'
                        onPress={() => impersonateUser(selectedStaff.key)}
                        isLoading={deleteLoading || toggleLoading || releaseLoading}
                      >
                        <UserIcon className='size-4' title='Impersonate user' />
                        Impersonate User
                      </Button>
                      {!isReleasedStaffEmail(selectedStaff.emailAddress) && (
                        <Button
                          size='sm'
                          variant='flat'
                          color='warning'
                          onPress={() => {
                            openReleaseEmail(selectedStaff);
                            onCloseDrawer();
                          }}
                          isLoading={releaseLoading}
                        >
                          <EnvelopeIcon className='size-4' title='Release email' />
                          {staffEmailCopy.freeEmailButton}
                        </Button>
                      )}
                      {selectedStaff.canDelete && (
                        <Button
                          size='sm'
                          variant='flat'
                          color='danger'
                          onPress={() => {
                            openDeleteStaff(selectedStaff);
                            onCloseDrawer();
                          }}
                          isLoading={deleteLoading}
                        >
                          <TrashIcon className='size-4' title='Delete staff member' />
                          Delete Staff
                        </Button>
                      )}
                    </SecurityCheck>
                  </div>
                </div>
                <div>
                  <h6 className='text-sm font-semibold text-gray-900 mb-3'>
                    Accounts ({selectedStaff.accountSiteStaffMembers.length})
                  </h6>
                  <div
                    className='grid gap-3'
                    style={{
                      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                    }}
                  >
                    {selectedStaff.accountSiteStaffMembers?.map(accountSiteStaff => {
                      return (
                        <div
                          key={accountSiteStaff.id}
                          className={cn(
                            'border rounded-lg p-4 flex-col bg-gray-50',
                            canEditStaffAccounts && 'cursor-pointer hover:bg-gray-100',
                          )}
                          onClick={() => {
                            if (canEditStaffAccounts) {
                              navigate(`/staff/edit/${selectedStaff.key}?tab=accounts`);
                            }
                          }}
                        >
                          <div className='flex items-center justify-between gap-2 border-b pb-2 mb-2'>
                            <p className='font-bold text-base'>{accountSiteStaff.accountName}</p>
                            <p className='text-xs text-gray-500 italic'>
                              added {formatDate(accountSiteStaff.addedAt, DateTime.DATE_SHORT)}
                            </p>
                          </div>
                          {accountSiteStaff.accountDeactivatedAt && (
                            <p className='text-xs text-red-500 italic'>
                              account deactivated{' '}
                              {formatDate(
                                accountSiteStaff.accountDeactivatedAt,
                                DateTime.DATE_SHORT,
                              )}
                            </p>
                          )}
                          <div className='flex flex-col gap-1 mt-2'>
                            <div className='flex items-center gap-2 text-meta'>
                              <IdentificationIcon className='size-4 text-gray-400' />
                              <p>{accountSiteStaff.position || 'No position listed'}</p>
                            </div>
                            <div className='flex items-center gap-2 text-meta'>
                              <KeyIcon className='size-4 text-gray-400' />
                              <p>{accountSiteStaff.staffRole.description}</p>
                            </div>
                            <p>{accountSiteStaff.staffMemberCategory?.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default StaffMemberTable;
